/**
 * Wire contracts shared by the Worker and the SPA.
 *
 * The API always returns these camelCased DTOs — never raw parser output — so the
 * frontend is insulated from feed-shape churn and new namespace phases.
 */

// ------------------------------------------------------------ Podcasting 2.0 values

export interface Funding {
  url: string;
  label?: string;
}

export interface Person {
  name: string;
  role?: string;
  group?: string;
  img?: string;
  href?: string;
}

export interface Location {
  name?: string;
  geo?: string;
  osm?: string;
}

export interface Transcript {
  url: string;
  type?: string;
  language?: string;
  /** `captions` marks a closed-caption file rather than a reading transcript. */
  rel?: string;
}

export interface Soundbite {
  startTime: number;
  duration: number;
  title?: string;
}

export interface Trailer {
  url: string;
  title?: string;
  pubDate?: number;
  length?: number;
  type?: string;
  season?: number;
}

export interface ValueRecipient {
  name?: string;
  type?: string;
  address?: string;
  split?: number;
  customKey?: string;
  customValue?: string;
  fee?: boolean;
}

export interface ValueBlock {
  type?: string;
  method?: string;
  suggested?: string;
  recipients: ValueRecipient[];
}

export interface AlternateEnclosureSource {
  uri: string;
  contentType?: string;
}

export interface AlternateEnclosure {
  type?: string;
  length?: number;
  bitrate?: number;
  height?: number;
  lang?: string;
  title?: string;
  rel?: string;
  codecs?: string;
  default?: boolean;
  sources: AlternateEnclosureSource[];
  integrity?: { type?: string; value?: string };
}

export interface SocialInteract {
  uri?: string;
  protocol?: string;
  accountId?: string;
  accountUrl?: string;
  priority?: number;
}

export interface RemoteItem {
  feedGuid?: string;
  feedUrl?: string;
  itemGuid?: string;
  medium?: string;
  title?: string;
}

export interface TxtRecord {
  purpose?: string;
  value: string;
}

export interface ChatConfig {
  server?: string;
  protocol?: string;
  accountId?: string;
  space?: string;
}

export interface UpdateFrequency {
  text?: string;
  complete?: boolean;
  dtstart?: string;
  rrule?: string;
}

export interface Chapter {
  startTime: number;
  title?: string;
  img?: string;
  url?: string;
  endTime?: number;
  toc?: boolean;
}

export interface TranscriptCue {
  startTime: number;
  endTime?: number;
  speaker?: string;
  body: string;
}

export interface NormalizedTranscript {
  format: 'vtt' | 'srt' | 'json' | 'html' | 'text';
  language?: string;
  cues: TranscriptCue[];
  /** Populated for HTML/plain transcripts that carry no timing information. */
  html?: string;
}

/** Podcasting 2.0 phases a feed was observed to use — mirrors partytime's `pc20support`. */
export type Pc20Phase = 1 | 2 | 3 | 4 | 5 | 6 | 7;

// ------------------------------------------------------------------------ API DTOs

export interface FeedDto {
  id: number;
  podcastGuid: string | null;
  feedUrl: string;
  title: string;
  author: string | null;
  description: string | null;
  imageUrl: string | null;
  language: string | null;
  link: string | null;
  itunesType: string | null;
  explicit: boolean;
  complete: boolean;
  locked: boolean;
  medium: string | null;
  categories: string[];
  funding: Funding[];
  value: ValueBlock | null;
  persons: Person[];
  location: Location | null;
  trailers: Trailer[];
  podroll: RemoteItem[];
  publisher: RemoteItem[];
  txt: TxtRecord[];
  chat: ChatConfig | null;
  license: { identifier?: string; url?: string } | null;
  updateFrequency: UpdateFrequency | null;
  pc20Phases: Pc20Phase[];
  episodeCount: number;
  lastFetchedAt: number | null;
  subscribed?: boolean;
}

export interface EpisodeDto {
  id: number;
  feedId: number;
  guid: string;
  title: string;
  description: string | null;
  link: string | null;
  enclosureUrl: string;
  enclosureType: string | null;
  enclosureLen: number | null;
  durationSec: number | null;
  pubDate: number | null;
  season: number | null;
  episodeNum: number | null;
  episodeType: string | null;
  explicit: boolean;
  imageUrl: string | null;
  chaptersUrl: string | null;
  transcripts: Transcript[];
  soundbites: Soundbite[];
  persons: Person[];
  alternateEnclosures: AlternateEnclosure[];
  socialInteract: SocialInteract[];
  value: ValueBlock | null;
  /** Joined from the caller's `playback_state` when authenticated. */
  progress?: ProgressDto | null;
  /** Joined feed context so episode lists render without an N+1 fetch. */
  feedTitle?: string;
  feedImageUrl?: string | null;
}

export interface ProgressDto {
  episodeId: number;
  positionSec: number;
  durationSec: number | null;
  completed: boolean;
  playbackRate: number | null;
  updatedAt: number;
}

export interface QueueItemDto {
  episode: EpisodeDto;
  sortKey: number;
  addedAt: number;
}

export interface PlaylistSummaryDto {
  id: number;
  name: string;
  createdAt: number;
  itemCount: number;
}

export interface PlaylistItemDto {
  sortKey: number;
  episode: EpisodeDto;
}

export interface PlaylistDetailDto {
  playlist: {
    id: number;
    name: string;
    userId: string;
    createdAt: number;
  };
  items: PlaylistItemDto[];
}

export interface LiveItemDto {
  id: number;
  feedId: number;
  guid: string;
  title: string;
  description: string | null;
  status: string;
  startTime: number | null;
  endTime: number | null;
  enclosureUrl: string;
  enclosureType: string | null;
  chat: ChatConfig | null;
  imageUrl: string | null;
  updatedAt: number;
  feedTitle?: string;
  feedAuthor?: string | null;
  feedImageUrl?: string | null;
  subscribed?: boolean;
  feed?: {
    id: number;
    title: string;
    author?: string;
    imageUrl?: string;
  };
}

export interface UserDto {
  id: string;
  email: string | null;
  displayName: string | null;
  createdAt: number;
  settings: UserSettings;
  hasPasskey: boolean;
}

export interface UserSettings {
  defaultPlaybackRate?: number;
  skipForwardSec?: number;
  skipBackSec?: number;
  theme?: 'system' | 'light' | 'dark';
  autoAddNewEpisodesToQueue?: boolean;
}

export interface SearchResultDto {
  feedUrl: string;
  title: string;
  author?: string;
  imageUrl?: string;
  description?: string;
  episodeCount?: number;
  podcastGuid?: string;
  /** Set when this show already exists in the local library. */
  feedId?: number;
}

export interface PersonDetailDto {
  name: string;
  role?: string;
  group?: string;
  img?: string;
  href?: string;
  shows: FeedDto[];
  episodes: EpisodeDto[];
}

export interface ApiError {
  error: string;
  message: string;
}

export interface OpmlImportItemResult {
  url: string;
  title?: string;
  status: 'imported' | 'already_subscribed' | 'failed';
  error?: string;
}

export interface OpmlImportResult {
  imported: number;
  alreadySubscribed: number;
  failed: number;
  total: number;
  results: OpmlImportItemResult[];
}

export interface PushSubscriptionPayload {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
  userAgent?: string;
}

export interface PushSubscriptionDto {
  id: string;
  userId: string;
  endpoint: string;
  p256dh: string;
  auth: string;
  userAgent?: string | null;
  createdAt: number;
  lastUsedAt: number;
}

export const DEFAULT_SETTINGS: Required<
  Pick<UserSettings, 'defaultPlaybackRate' | 'skipForwardSec' | 'skipBackSec' | 'theme'>
> = {
  defaultPlaybackRate: 1,
  skipForwardSec: 30,
  skipBackSec: 15,
  theme: 'system',
};

// ------------------------------------------------------------------ Live Chat DTOs

export interface ChatMessage {
  id: string;
  text: string;
  displayName?: string;
  timestamp: number;
  protocol?: 'native' | 'irc' | 'nostr' | 'matrix';
  senderColor?: string;
}

export interface RoomStateMessage {
  type: 'room_state';
  status: 'live';
  listenerCount: number;
  recentMessages: ChatMessage[];
}

export interface BroadcastChatMessage extends ChatMessage {
  type: 'chat';
}

export interface BroadcastReactionMessage {
  type: 'reaction';
  emoji: string;
}

export interface PongMessage {
  type: 'pong';
}

export interface ListenerCountMessage {
  type: 'listener_count';
  count: number;
}

export interface StatusChangeMessage {
  type: 'status_change';
  status: 'live' | 'ended';
}

export interface ErrorMessage {
  type: 'error';
  message: string;
}

export type ServerMessage =
  | RoomStateMessage
  | BroadcastChatMessage
  | BroadcastReactionMessage
  | PongMessage
  | ListenerCountMessage
  | StatusChangeMessage
  | ErrorMessage;

